import merge from 'lodash/merge';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { frFR } from '@material-ui/core/locale';
import { THEMES } from '../constants';
import { lightShadows, darkShadows } from './shadows';
import buildComponents from './components';
import TONES from './tones';
import {
  brand,
  accent,
  neutral,
  semantic,
  gradients,
  FONT_SANS,
  FONT_DISPLAY,
  RADIUS
} from './tokens';

/* ------------------------------------------------------------------ *
 *  BASE — typographie & formes communes aux deux thèmes.
 * ------------------------------------------------------------------ */

const baseOptions = {
  direction: 'ltr',
  shape: {
    borderRadius: RADIUS
  },
  typography: {
    fontFamily: FONT_SANS,
    button: {
      fontWeight: 600,
      letterSpacing: '0.005em'
    },
    h1: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 800,
      fontSize: '3.25rem',
      lineHeight: 1.12,
      letterSpacing: '-0.035em'
    },
    h2: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 800,
      fontSize: '2.5rem',
      lineHeight: 1.16,
      letterSpacing: '-0.03em'
    },
    h3: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.2,
      letterSpacing: '-0.025em'
    },
    h4: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      fontSize: '1.7rem',
      lineHeight: 1.25,
      letterSpacing: '-0.022em'
    },
    h5: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      fontSize: '1.35rem',
      lineHeight: 1.3,
      letterSpacing: '-0.018em'
    },
    h6: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      fontSize: '1.075rem',
      lineHeight: 1.4,
      letterSpacing: '-0.012em'
    },
    subtitle1: {
      fontWeight: 600,
      fontSize: '0.975rem',
      letterSpacing: '-0.005em'
    },
    subtitle2: {
      fontWeight: 600,
      fontSize: '0.875rem',
      letterSpacing: '-0.005em'
    },
    body1: {
      fontSize: '0.925rem',
      lineHeight: 1.6
    },
    body2: {
      fontSize: '0.85rem',
      lineHeight: 1.6
    },
    caption: {
      fontSize: '0.75rem',
      letterSpacing: '0.01em'
    },
    overline: {
      fontWeight: 700,
      fontSize: '0.7rem',
      letterSpacing: '0.1em',
      lineHeight: 2
    }
  }
};

/* ------------------------------------------------------------------ *
 *  Palettes « métier » historiques.
 *  Ces clés sont consommées un peu partout dans l'application
 *  (chips de statut, flèches, indicateurs de performance…) : elles sont
 *  conservées à l'identique pour ne rien casser fonctionnellement.
 * ------------------------------------------------------------------ */

const businessPalette = {
  // Tons sémantiques des pastilles de statut, exposés dans la palette pour
  // qu'un `sx` puisse écrire `alpha(theme.palette.tones.indigo.base, 0.2)`
  // sans importer quoi que ce soit.
  tones: TONES,
  inscritD: {
    contrastText: '#ffffff',
    main: '#00695f'
  },
  profession: {
    contrastText: '#ffffff',
    main: '#d443ff'
  },
  formation: {
    contrastText: '#ffffff',
    main: '#00695f'
  },
  partenaires: {
    contrastText: '#ffffff',
    main: '#854c85'
  },
  prestations: {
    contrastText: '#ffffff',
    main: '#9670b3'
  },
  menu: {
    primary: '#ffffff',
    secondary: '#ffb476',
    active: '#88aaee'
  },
  arrowsColor: {
    main: '#7eb627',
    contrastText: '#ffffff'
  }
};

const themesOptions = {
  [THEMES.LIGHT]: {
    components: buildComponents('light'),
    palette: {
      ...businessPalette,
      mode: 'light',
      action: {
        active: neutral[500],
        hover: 'rgba(27, 36, 69, 0.04)',
        selected: 'rgba(59, 130, 246, 0.08)',
        focus: 'rgba(59, 130, 246, 0.12)'
      },
      background: {
        default: '#f5f7fb',
        paper: '#ffffff'
      },
      divider: '#e8ecf3',
      neutral,
      gradients,
      primary: {
        contrastText: '#ffffff',
        main: brand[700],
        light: brand[400],
        dark: brand[900]
      },
      secondary: {
        contrastText: '#ffffff',
        main: accent[600],
        light: accent[500],
        dark: accent[800]
      },
      error: {
        contrastText: '#ffffff',
        main: semantic.error.main,
        light: semantic.error.light,
        dark: semantic.error.dark
      },
      warning: {
        contrastText: '#ffffff',
        main: semantic.warning.main,
        light: semantic.warning.light,
        dark: semantic.warning.dark
      },
      success: {
        contrastText: '#ffffff',
        main: semantic.success.main,
        light: semantic.success.light,
        dark: semantic.success.dark
      },
      info: {
        contrastText: '#ffffff',
        main: semantic.info.main,
        light: semantic.info.light,
        dark: semantic.info.dark
      },
      text: {
        primary: '#111a2e',
        secondary: '#64748b',
        disabled: '#94a3b8'
      },
      dialogText: {
        contrastText: '#ffffff',
        main: '#808080' // « grey » en CSS : alpha() refuse les noms de couleur
      },
      performanceGreen: {
        contrastText: '#ffffff',
        main: '#0f9d58'
      },
      performanceRed: {
        contrastText: '#ffffff',
        main: semantic.error.main
      }
    },
    shadows: lightShadows
  },

  [THEMES.DARK]: {
    components: buildComponents('dark'),
    palette: {
      ...businessPalette,
      mode: 'dark',
      action: {
        active: neutral[400],
        hover: 'rgba(255, 255, 255, 0.05)',
        selected: 'rgba(96, 165, 250, 0.14)',
        focus: 'rgba(96, 165, 250, 0.2)'
      },
      background: {
        default: '#0d1420',
        paper: '#161e2d'
      },
      divider: 'rgba(148, 163, 184, 0.16)',
      neutral,
      gradients,
      primary: {
        contrastText: '#1b2445',
        main: '#edb382',
        light: '#f6cba6',
        dark: '#d59763'
      },
      secondary: {
        contrastText: '#0d1420',
        main: accent[500],
        light: accent[400],
        dark: accent[700]
      },
      error: {
        contrastText: '#ffffff',
        main: semantic.error.main,
        light: semantic.error.light,
        dark: semantic.error.dark
      },
      warning: {
        contrastText: '#0d1420',
        main: semantic.warning.main,
        light: semantic.warning.light,
        dark: semantic.warning.dark
      },
      success: {
        contrastText: '#0d1420',
        main: semantic.success.main,
        light: semantic.success.light,
        dark: semantic.success.dark
      },
      info: {
        contrastText: '#0d1420',
        main: semantic.info.main,
        light: semantic.info.light,
        dark: semantic.info.dark
      },
      text: {
        primary: '#e8edf6',
        secondary: '#94a3b8',
        disabled: '#64748b'
      },
      dialogText: {
        contrastText: '#ffffff',
        main: '#ffffff'
      },
      performanceGreen: {
        contrastText: '#ffffff',
        main: '#7eb627'
      },
      performanceRed: {
        contrastText: '#ffffff',
        main: semantic.error.main
      }
    },
    shadows: darkShadows
  }
};

export const createCustomTheme = (config = {}) => {
  let themeOptions = themesOptions[config.theme];

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    themeOptions = themesOptions[THEMES.LIGHT];
  }

  let theme = createTheme(merge({}, baseOptions, themeOptions, {
    ...(config.roundedCorners && {
      shape: {
        borderRadius: 16
      }
    })
  }, {
    direction: config.direction
  }), frFR);

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};

export { brand, accent, neutral, semantic, gradients } from './tokens';
